import { formatAbiItem, formatAbiParams } from '../utils/abi/formatAbiItem.js';
import { size } from '../utils/data/size.js';
import { BaseError } from './base.js';
export class AbiConstructorNotFoundError extends BaseError {
  constructor(_ref) {
    let {
      docsPath
    } = _ref;
    super(['A constructor was not found on the ABI.', 'Make sure you are using the correct ABI and that the constructor exists on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiConstructorNotFoundError'
    });
  }
}
export class AbiConstructorParamsNotFoundError extends BaseError {
  constructor(_ref2) {
    let {
      docsPath
    } = _ref2;
    super(['Constructor arguments were provided (`args`), but a constructor parameters (`inputs`) were not found on the ABI.', 'Make sure you are using the correct ABI, and that the `inputs` attribute on the constructor exists.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiConstructorParamsNotFoundError'
    });
  }
}
export class AbiDecodingDataSizeInvalidError extends BaseError {
  constructor(_ref3) {
    let {
      data,
      size
    } = _ref3;
    super(["Data size of ".concat(size, " bytes is invalid."), 'Size must be in increments of 32 bytes (size % 32 === 0).'].join('\n'), {
      metaMessages: ["Data: ".concat(data, " (").concat(size, " bytes)")]
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiDecodingDataSizeInvalidError'
    });
  }
}
export class AbiDecodingDataSizeTooSmallError extends BaseError {
  constructor(_ref4) {
    let {
      data,
      params,
      size
    } = _ref4;
    super(["Data size of ".concat(size, " bytes is too small for given parameters.")].join('\n'), {
      metaMessages: ["Params: (".concat(formatAbiParams(params, {
        includeName: true
      }), ")"), "Data:   ".concat(data, " (").concat(size, " bytes)")]
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiDecodingDataSizeTooSmallError'
    });
    Object.defineProperty(this, "data", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "params", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "size", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.data = data;
    this.params = params;
    this.size = size;
  }
}
export class AbiDecodingZeroDataError extends BaseError {
  constructor() {
    super('Cannot decode zero data ("0x") with ABI parameters.');
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiDecodingZeroDataError'
    });
  }
}
export class AbiEncodingArrayLengthMismatchError extends BaseError {
  constructor(_ref5) {
    let {
      expectedLength,
      givenLength,
      type
    } = _ref5;
    super(["ABI encoding array length mismatch for type ".concat(type, "."), "Expected length: ".concat(expectedLength), "Given length: ".concat(givenLength)].join('\n'));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEncodingArrayLengthMismatchError'
    });
  }
}
export class AbiEncodingBytesSizeMismatchError extends BaseError {
  constructor(_ref6) {
    let {
      expectedSize,
      value
    } = _ref6;
    super("Size of bytes \"".concat(value, "\" (bytes").concat(size(value), ") does not match expected size (bytes").concat(expectedSize, ")."));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEncodingBytesSizeMismatchError'
    });
  }
}
export class AbiEncodingLengthMismatchError extends BaseError {
  constructor(_ref7) {
    let {
      expectedLength,
      givenLength
    } = _ref7;
    super(['ABI encoding params/values length mismatch.', "Expected length (params): ".concat(expectedLength), "Given length (values): ".concat(givenLength)].join('\n'));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEncodingLengthMismatchError'
    });
  }
}
export class AbiErrorInputsNotFoundError extends BaseError {
  constructor(errorName, _ref8) {
    let {
      docsPath
    } = _ref8;
    super(["Arguments (`args`) were provided to \"".concat(errorName, "\", but \"").concat(errorName, "\" on the ABI does not contain any parameters (`inputs`)."), 'Cannot encode error result without knowing what the parameter types are.', 'Make sure you are using the correct ABI and that the inputs exist on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiErrorInputsNotFoundError'
    });
  }
}
export class AbiErrorNotFoundError extends BaseError {
  constructor(errorName) {
    let {
      docsPath
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(["Error ".concat(errorName ? "\"".concat(errorName, "\" ") : '', "not found on ABI."), 'Make sure you are using the correct ABI and that the error exists on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiErrorNotFoundError'
    });
  }
}
export class AbiErrorSignatureNotFoundError extends BaseError {
  constructor(signature, _ref9) {
    let {
      docsPath
    } = _ref9;
    super(["Encoded error signature \"".concat(signature, "\" not found on ABI."), 'Make sure you are using the correct ABI and that the error exists on it.', "You can look up the decoded signature here: https://openchain.xyz/signatures?query=".concat(signature, ".")].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiErrorSignatureNotFoundError'
    });
    Object.defineProperty(this, "signature", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.signature = signature;
  }
}
export class AbiEventSignatureEmptyTopicsError extends BaseError {
  constructor(_ref10) {
    let {
      docsPath
    } = _ref10;
    super('Cannot extract event signature from empty topics.', {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEventSignatureEmptyTopicsError'
    });
  }
}
export class AbiEventSignatureNotFoundError extends BaseError {
  constructor(signature, _ref11) {
    let {
      docsPath
    } = _ref11;
    super(["Encoded event signature \"".concat(signature, "\" not found on ABI."), 'Make sure you are using the correct ABI and that the event exists on it.', "You can look up the signature here: https://openchain.xyz/signatures?query=".concat(signature, ".")].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEventSignatureNotFoundError'
    });
  }
}
export class AbiEventNotFoundError extends BaseError {
  constructor(eventName) {
    let {
      docsPath
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(["Event ".concat(eventName ? "\"".concat(eventName, "\" ") : '', "not found on ABI."), 'Make sure you are using the correct ABI and that the event exists on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiEventNotFoundError'
    });
  }
}
export class AbiFunctionNotFoundError extends BaseError {
  constructor(functionName) {
    let {
      docsPath
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(["Function ".concat(functionName ? "\"".concat(functionName, "\" ") : '', "not found on ABI."), 'Make sure you are using the correct ABI and that the function exists on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiFunctionNotFoundError'
    });
  }
}
export class AbiFunctionOutputsNotFoundError extends BaseError {
  constructor(functionName, _ref12) {
    let {
      docsPath
    } = _ref12;
    super(["Function \"".concat(functionName, "\" does not contain any `outputs` on ABI."), 'Cannot decode function result without knowing what the parameter types are.', 'Make sure you are using the correct ABI and that the function exists on it.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiFunctionOutputsNotFoundError'
    });
  }
}
export class AbiFunctionSignatureNotFoundError extends BaseError {
  constructor(signature, _ref13) {
    let {
      docsPath
    } = _ref13;
    super(["Encoded function signature \"".concat(signature, "\" not found on ABI."), 'Make sure you are using the correct ABI and that the function exists on it.', "You can look up the signature here: https://openchain.xyz/signatures?query=".concat(signature, ".")].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiFunctionSignatureNotFoundError'
    });
  }
}
export class AbiItemAmbiguityError extends BaseError {
  constructor(x, y) {
    super('Found ambiguous types in overloaded ABI items.', {
      metaMessages: ["`".concat(x.type, "` in `").concat(formatAbiItem(x.abiItem), "`, and"), "`".concat(y.type, "` in `").concat(formatAbiItem(y.abiItem), "`"), '', 'These types encode differently and cannot be distinguished at runtime.', 'Remove one of the ambiguous items in the ABI.']
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'AbiItemAmbiguityError'
    });
  }
}
export class BytesSizeMismatchError extends BaseError {
  constructor(_ref14) {
    let {
      expectedSize,
      givenSize
    } = _ref14;
    super("Expected bytes".concat(expectedSize, ", got bytes").concat(givenSize, "."));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'BytesSizeMismatchError'
    });
  }
}
export class DecodeLogDataMismatch extends BaseError {
  constructor(_ref15) {
    let {
      abiItem,
      data,
      params,
      size
    } = _ref15;
    super(["Data size of ".concat(size, " bytes is too small for non-indexed event parameters.")].join('\n'), {
      metaMessages: ["Params: (".concat(formatAbiParams(params, {
        includeName: true
      }), ")"), "Data:   ".concat(data, " (").concat(size, " bytes)")]
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'DecodeLogDataMismatch'
    });
    Object.defineProperty(this, "abiItem", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "data", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "params", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "size", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.abiItem = abiItem;
    this.data = data;
    this.params = params;
    this.size = size;
  }
}
export class DecodeLogTopicsMismatch extends BaseError {
  constructor(_ref16) {
    let {
      abiItem,
      param
    } = _ref16;
    super(["Expected a topic for indexed event parameter".concat(param.name ? " \"".concat(param.name, "\"") : '', " on event \"").concat(formatAbiItem(abiItem, {
      includeName: true
    }), "\".")].join('\n'));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'DecodeLogTopicsMismatch'
    });
    Object.defineProperty(this, "abiItem", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.abiItem = abiItem;
  }
}
export class InvalidAbiEncodingTypeError extends BaseError {
  constructor(type, _ref17) {
    let {
      docsPath
    } = _ref17;
    super(["Type \"".concat(type, "\" is not a valid encoding type."), 'Please provide a valid ABI type.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'InvalidAbiEncodingType'
    });
  }
}
export class InvalidAbiDecodingTypeError extends BaseError {
  constructor(type, _ref18) {
    let {
      docsPath
    } = _ref18;
    super(["Type \"".concat(type, "\" is not a valid decoding type."), 'Please provide a valid ABI type.'].join('\n'), {
      docsPath
    });
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'InvalidAbiDecodingType'
    });
  }
}
export class InvalidArrayError extends BaseError {
  constructor(value) {
    super(["Value \"".concat(value, "\" is not a valid array.")].join('\n'));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'InvalidArrayError'
    });
  }
}
export class InvalidDefinitionTypeError extends BaseError {
  constructor(type) {
    super(["\"".concat(type, "\" is not a valid definition type."), 'Valid types: "function", "event", "error"'].join('\n'));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'InvalidDefinitionTypeError'
    });
  }
}
export class UnsupportedPackedAbiType extends BaseError {
  constructor(type) {
    super("Type \"".concat(type, "\" is not supported for packed encoding."));
    Object.defineProperty(this, "name", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 'UnsupportedPackedAbiType'
    });
  }
}
//# sourceMappingURL=abi.js.map